/**
 * Runtime configuration — and since 2026-08-11 that word is accurate.
 *
 * The contract says it plainly: "clients must take the base URL from
 * configuration and never hard-code it." The `*.run.app` hostnames in the
 * `servers` block are correct today and are explicitly not a commitment, so the
 * only value baked in here is the local mock, and only as a dev default.
 *
 * # Why this reads from the page and not only from `import.meta.env`
 *
 * `VITE_POKPOK_API_BASE_URL` is substituted **at build time**, which means the
 * URL stops being configuration the moment the bundle is emitted: it is baked
 * into the artifact. For a static site that is the normal trade, and here it
 * collides with a decision this estate has already made and cares about.
 *
 * ADR-0011: **"Prod is promoted, never rebuilt. The artifact that reaches
 * merchants is the exact image staging already exercised."** A bundle with
 * staging's API URL inside it cannot be the artifact prod runs, so a build-time
 * URL forces one image per environment and quietly deletes the promotion
 * guarantee for this surface — the guest-facing one, where a wrong base URL
 * means every guest sees a broken menu.
 *
 * So the container injects it instead. `index.html` carries a placeholder that
 * the image's entrypoint rewrites from `POKPOK_API_BASE_URL` at start, and one
 * image serves both environments. It is injected into the HTML rather than
 * fetched from a `/config.json` on purpose: `03-qr-ordering.md`'s perf budget
 * is why this repo has almost no dependencies, and a blocking request before
 * first render would spend on configuration what that budget saved on code.
 *
 * Precedence is injected → build-time env → dev default, so the dev server is
 * untouched (its placeholder is never rewritten and is ignored) and a build-time
 * value still works for anyone serving `dist/` directly.
 */

const DEV_DEFAULT_BASE_URL = 'http://localhost:8080';

/**
 * The literal token sitting in `index.html` until an entrypoint replaces it.
 *
 * Seeing it at runtime is not an error — it is what the dev server and a plain
 * `vite preview` both show — so it is treated as "nobody injected anything" and
 * the next source is tried. Checking for it matters: without this the dev server
 * would resolve its base URL to the string `__POKPOK_API_BASE_URL__` and every
 * request would go to a path made of that, which fails far from its cause.
 */
const UNREPLACED = '__POKPOK_API_BASE_URL__';

export interface AppConfig {
  apiBaseUrl: string;
}

interface InjectedRuntime {
  apiBaseUrl?: string;
}

/** Strip trailing slashes so callers can always join with a leading-slash path. */
function normalise(value: string): string {
  return value.replace(/\/+$/, '');
}

function readInjected(): string | undefined {
  const injected = (globalThis as {__POKPOK_RUNTIME__?: InjectedRuntime})
    .__POKPOK_RUNTIME__;
  const value = injected?.apiBaseUrl?.trim();
  if (!value || value === UNREPLACED) return undefined;
  return value;
}

export function readConfig(env: ImportMetaEnv = import.meta.env): AppConfig {
  const injected = readInjected();
  if (injected) return {apiBaseUrl: normalise(injected)};

  const configured = env.VITE_POKPOK_API_BASE_URL?.trim();
  if (configured) return {apiBaseUrl: normalise(configured)};

  if (env.DEV) return {apiBaseUrl: DEV_DEFAULT_BASE_URL};

  // A production build with no base URL is a deployment mistake, and failing
  // here names it. The alternative is every request 404ing against the CDN.
  throw new Error(
    'No API base URL. The container injects POKPOK_API_BASE_URL into index.html at start; ' +
      'VITE_POKPOK_API_BASE_URL is the build-time fallback. See .env.example — ' +
      'the API base URL is configuration, not a constant.',
  );
}
